import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { IoSearchOutline } from 'react-icons/io5';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import { CustomDatePicker } from '../commons/datepicker';
import '../../styles/ReportAssignmentModal.css';

const ReportAssignmentModal = ({ isOpen, onClose, template, schoolId }) => {
    const { t } = useTranslation('reports');
    // Current step: 1 = select dogs, 2 = configure settings
    const [currentStep, setCurrentStep] = useState(1);

    // Dogs data
    const [dogs, setDogs] = useState([]);
    const [filteredDogs, setFilteredDogs] = useState([]);
    const [selectedDogIds, setSelectedDogIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Assignment settings (Step 2)
    const [settings, setSettings] = useState({
        recurrence: 'DAILY',
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
    });

    // UI states
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Recurrence options
    const recurrenceOptions = [
        { value: 'ONCE', label: t('recurrence.once') },
        { value: 'DAILY', label: t('recurrence.daily') },
        { value: 'EVERY_2_DAYS', label: t('recurrence.every2Days') },
        { value: 'EVERY_3_DAYS', label: t('recurrence.every3Days') },
        { value: 'EVERY_4_DAYS', label: t('recurrence.every4Days') },
        { value: 'EVERY_5_DAYS', label: t('recurrence.every5Days') },
        { value: 'EVERY_6_DAYS', label: t('recurrence.every6Days') },
        { value: 'WEEKLY', label: t('recurrence.weekly') }
    ];

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setSearchTerm('');
            setError('');
            setSuccessMessage('');
            setSettings({
                recurrence: 'DAILY',
                startDate: new Date().toISOString().split('T')[0],
                endDate: ''
            });
        }
    }, [isOpen]);

    // Fetch data when modal opens
    useEffect(() => {
        if (isOpen && template && schoolId) {
            fetchData();
        }
    }, [isOpen, template, schoolId]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            setError('');

            if (!template || !template.id || !schoolId) {
                setError(t('invalidTemplateData'));
                setIsLoading(false);
                return;
            }

            const token = localStorage.getItem('jwt');

            // Fetch all dogs for the school
            const dogsResponse = await axios.get(`/api/v1/dog/list/${schoolId}/booking`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch current assignments for this template
            let currentAssignments = [];
            try {
                const assignmentsResponse = await axios.get(`/api/v1/reports/assignments/report/${template.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                currentAssignments = assignmentsResponse.data || [];
            } catch (err) {
                if (err.response?.status !== 404) {
                    console.error('Error fetching assignments:', err);
                }
            }

            // Create set of assigned dog IDs
            const assignedDogIds = new Set(currentAssignments.map(a => String(a.dogId)));

            // Mark which dogs have the template assigned
            const dogsWithStatus = dogsResponse.data.map(dog => ({
                ...dog,
                hasTemplateAssigned: assignedDogIds.has(String(dog.id)),
                assignmentId: currentAssignments.find(a => String(a.dogId) === String(dog.id))?.id
            }));

            setDogs(dogsWithStatus);
            setFilteredDogs(dogsWithStatus);

            // Pre-select already assigned dogs
            const preSelected = dogsWithStatus
                .filter(dog => dog.hasTemplateAssigned)
                .map(dog => String(dog.id));
            setSelectedDogIds(preSelected);

        } catch (err) {
            console.error('Error fetching data:', err);
            setError(t('fetchDataError'));
        } finally {
            setIsLoading(false);
        }
    };

    // Search handler
    const handleSearch = (term) => {
        setSearchTerm(term);

        if (!term.trim()) {
            setFilteredDogs(dogs);
            return;
        }

        const lowerCaseTerm = term.toLowerCase();
        const filtered = dogs.filter(dog =>
            (dog.name && dog.name.toLowerCase().includes(lowerCaseTerm)) ||
            (dog.breed && dog.breed.toLowerCase().includes(lowerCaseTerm)) ||
            (dog.ownersName && dog.ownersName.toLowerCase().includes(lowerCaseTerm))
        );

        setFilteredDogs(filtered);
    };

    // Selection handlers
    const toggleDogSelection = (dogId) => {
        const dogIdStr = String(dogId);
        setSelectedDogIds(prev => {
            if (prev.includes(dogIdStr)) {
                return prev.filter(id => id !== dogIdStr);
            } else {
                return [...prev, dogIdStr];
            }
        });
    };

    const selectAll = () => {
        const allIds = filteredDogs.map(dog => String(dog.id));
        setSelectedDogIds(prev => {
            const newSet = new Set([...prev, ...allIds]);
            return Array.from(newSet);
        });
    };

    const deselectAll = () => {
        setSelectedDogIds([]);
    };

    // Navigation handlers
    const goToStep2 = () => {
        setError('');
        setCurrentStep(2);
    };

    const goBackToStep1 = () => {
        setCurrentStep(1);
        setError('');
    };

    // Settings handlers
    const updateSetting = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Submit handler
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSuccessMessage('');
        setError('');

        try {
            const token = localStorage.getItem('jwt');

            // Get currently assigned dog IDs
            const currentlyAssignedIds = dogs
                .filter(dog => dog.hasTemplateAssigned)
                .map(dog => String(dog.id));

            // Determine changes
            const toAssign = selectedDogIds.filter(id => !currentlyAssignedIds.includes(id));
            const toUnassign = currentlyAssignedIds.filter(id => !selectedDogIds.includes(id));

            // Create new assignments
            await Promise.all(toAssign.map(dogIdStr => {
                const dog = dogs.find(d => String(d.id) === dogIdStr);
                return axios.post('/api/v1/reports/assignments', {
                    reportTemplateId: template.id,
                    dogId: dog.id,
                    recurrence: settings.recurrence,
                    startDate: settings.startDate,
                    endDate: settings.endDate || null
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }));

            // Remove old assignments
            await Promise.all(toUnassign.map(dogIdStr => {
                const dog = dogs.find(d => String(d.id) === dogIdStr);
                if (dog?.assignmentId) {
                    return axios.delete(`/api/v1/reports/assignments/${dog.assignmentId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }
                return Promise.resolve();
            }));

            const totalChanges = toAssign.length + toUnassign.length;

            if (totalChanges > 0) {
                setSuccessMessage(t('savedChanges', { added: toAssign.length, removed: toUnassign.length }));
            } else {
                setSuccessMessage(t('noChangesToSave'));
            }

            // Refresh data and go back to step 1
            await fetchData();

            // Close after short delay to show success message
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (err) {
            console.error('Error updating assignments:', err);
            setError(t('saveError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle click outside modal to close
    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('ram-overlay')) {
            onClose();
        }
    };

    if (!isOpen) return null;

    // Get currently assigned IDs for comparison
    const currentlyAssignedIds = dogs
        .filter(dog => dog.hasTemplateAssigned)
        .map(dog => String(dog.id));

    // Categorize dogs for Step 2
    const selectedDogsInfo = dogs.filter(dog => selectedDogIds.includes(String(dog.id)));

    // Dogs that will be newly assigned (selected but not currently assigned)
    const newDogsToAssign = selectedDogsInfo.filter(dog => !dog.hasTemplateAssigned);

    // Dogs that are already assigned and remain selected (no change)
    const alreadyAssignedDogs = selectedDogsInfo.filter(dog => dog.hasTemplateAssigned);

    // Dogs that were assigned but are now deselected (will be removed)
    const dogsToUnassign = dogs.filter(dog =>
        dog.hasTemplateAssigned && !selectedDogIds.includes(String(dog.id))
    );

    // Check if there are any changes to save
    const hasChanges = newDogsToAssign.length > 0 || dogsToUnassign.length > 0;

    return (
        <div className="ram-overlay" onClick={handleOverlayClick}>
            <div className="ram-modal">
                {/* Header */}
                <div className="ram-header">
                    {currentStep === 2 && (
                        <button className="ram-back-btn" onClick={goBackToStep1}>
                            <FiArrowLeft />
                        </button>
                    )}
                    <h2 className="ram-title">
                        {currentStep === 1 ? t('assign') : t('settings')}
                    </h2>
                    <div className="ram-step-indicator">
                        <span className={`ram-step ${currentStep >= 1 ? 'ram-step--active' : ''}`}>1</span>
                        <span className="ram-step-line"></span>
                        <span className={`ram-step ${currentStep >= 2 ? 'ram-step--active' : ''}`}>2</span>
                    </div>
                </div>

                <div className="ram-content">
                    {/* Messages */}
                    {successMessage && (
                        <div className="ram-success-message">
                            <FiCheck className="ram-message-icon" />
                            <p>{successMessage}</p>
                        </div>
                    )}

                    {error && (
                        <div className="ram-error-message">
                            <p>{error}</p>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="ram-loading">
                            <p>{t('loadingData')}</p>
                        </div>
                    ) : currentStep === 1 ? (
                        /* ========== STEP 1: SELECT DOGS ========== */
                        <>
                            {/* Search Bar */}
                            <div className="ram-search-bar">
                                <div className="ram-search-input-wrapper">
                                    <IoSearchOutline className="ram-search-icon" />
                                    <input
                                        type="text"
                                        placeholder={t('search')}
                                        value={searchTerm}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        className="ram-search-input"
                                    />
                                    {searchTerm && (
                                        <button
                                            className="ram-search-clear-btn"
                                            onClick={() => handleSearch('')}
                                            type="button"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Selection Controls */}
                            <div className="ram-selection-controls">
                                <span className="ram-selection-info">
                                    {t('selectedCount', { selected: selectedDogIds.length, total: filteredDogs.length })}
                                </span>
                                <div className="ram-selection-buttons">
                                    <button onClick={selectAll} className="ram-select-all-btn">
                                        {t('selectAll')}
                                    </button>
                                    <button onClick={deselectAll} className="ram-deselect-all-btn">
                                        {t('deselectAll')}
                                    </button>
                                </div>
                            </div>

                            {/* Dogs List */}
                            {filteredDogs.length === 0 ? (
                                <div className="ram-no-results">
                                    <p>{t('noDogsMatchSearch')}</p>
                                </div>
                            ) : (
                                <div className="ram-dogs-list">
                                    {filteredDogs.map(dog => {
                                        const isSelected = selectedDogIds.includes(String(dog.id));

                                        return (
                                            <div
                                                key={dog.id}
                                                className={`ram-dog-item ${isSelected ? 'ram-dog-item--selected' : ''}`}
                                                onClick={() => toggleDogSelection(dog.id)}
                                            >
                                                <div className="ram-dog-main">
                                                    <div className="ram-dog-info">
                                                        <h3 className="ram-dog-name">{dog.name}</h3>
                                                        <p className="ram-dog-breed">{dog.breed || t('noBreed')}</p>
                                                    </div>
                                                    {dog.hasTemplateAssigned && (
                                                        <div className="ram-dog-badge">
                                                            {t('assigned')}
                                                        </div>
                                                    )}
                                                </div>

                                                {(dog.ownersName || dog.ownerName) && (
                                                    <div className="ram-dog-owner">
                                                        <span className="ram-owner-label">{t('owner')}</span>
                                                        <span className="ram-owner-value">{dog.ownersName || dog.ownerName}</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    ) : (
                        /* ========== STEP 2: CONFIGURE SETTINGS ========== */
                        <div className="ram-settings-step">
                            {/* New Assignments Section */}
                            {newDogsToAssign.length > 0 && (
                                <>
                                    <div className="ram-section-header">
                                        <h3 className="ram-section-title">{t('newAssignments', { count: newDogsToAssign.length })}</h3>
                                    </div>

                                    {/* New Dogs Preview */}
                                    <div className="ram-selected-preview">
                                        <div className="ram-selected-tags">
                                            {newDogsToAssign.map(dog => (
                                                <span key={dog.id} className="ram-dog-tag ram-dog-tag--new">
                                                    {dog.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Settings Form - only for new assignments */}
                                    <div className="ram-settings-form">
                                        <div className="ram-form-group">
                                            <label className="ram-form-label">{t('recurrenceLabel')}</label>
                                            <select
                                                className="ram-form-select"
                                                value={settings.recurrence}
                                                onChange={(e) => updateSetting('recurrence', e.target.value)}
                                            >
                                                {recurrenceOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="ram-form-row">
                                            <div className="ram-form-group">
                                                <label className="ram-form-label">{t('startDate')}</label>
                                                <CustomDatePicker
                                                    name="startDate"
                                                    value={settings.startDate}
                                                    onChange={(e) => updateSetting('startDate', e.target.value)}
                                                />
                                            </div>
                                            <div className="ram-form-group">
                                                <label className="ram-form-label">{t('endDateOptional')}</label>
                                                <CustomDatePicker
                                                    name="endDate"
                                                    value={settings.endDate}
                                                    onChange={(e) => updateSetting('endDate', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Already Assigned Section */}
                            {alreadyAssignedDogs.length > 0 && (
                                <>
                                    <div className="ram-section-header">
                                        <h3 className="ram-section-title">{t('alreadyAssigned', { count: alreadyAssignedDogs.length })}</h3>
                                        <p className="ram-section-subtitle">{t('keepCurrentSettings')}</p>
                                    </div>

                                    <div className="ram-selected-preview ram-selected-preview--muted">
                                        <div className="ram-selected-tags">
                                            {alreadyAssignedDogs.map(dog => (
                                                <span key={dog.id} className="ram-dog-tag ram-dog-tag--existing">
                                                    {dog.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Dogs to Unassign Section */}
                            {dogsToUnassign.length > 0 && (
                                <>
                                    <div className="ram-section-header">
                                        <h3 className="ram-section-title ram-section-title--warning">{t('toRemove', { count: dogsToUnassign.length })}</h3>
                                        <p className="ram-section-subtitle">{t('willBeUnassigned')}</p>
                                    </div>

                                    <div className="ram-selected-preview ram-selected-preview--warning">
                                        <div className="ram-selected-tags">
                                            {dogsToUnassign.map(dog => (
                                                <span key={dog.id} className="ram-dog-tag ram-dog-tag--remove">
                                                    {dog.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* No changes message */}
                            {newDogsToAssign.length === 0 && dogsToUnassign.length === 0 && (
                                <div className="ram-no-changes">
                                    <p>{t('noChangesToSave')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="ram-footer">
                    <button onClick={onClose} className="ram-btn-cancel">
                        {t('cancel')}
                    </button>

                    {currentStep === 1 ? (
                        <button
                            onClick={goToStep2}
                            className="ram-btn-next"
                            disabled={!hasChanges}
                        >
                            {t('next')}
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            className="ram-btn-submit"
                            disabled={isSubmitting || !hasChanges}
                        >
                            {isSubmitting ? t('saving') : t('save')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportAssignmentModal;